import React from "react"

import { Col, Divider, Form, Modal, Row } from "antd"
import { RBHBox, RBHButton, RBHText, RBHTextInput } from "../../../../shared/components"
import { FormattedMessage, InjectedIntlProps, injectIntl } from "react-intl"
import { textStyles } from "../../../../shared/theme/fonts-ppv"
import { Colors } from "../../../../shared/theme"

import './styles.css'
import { FormInstance } from "antd/lib/form"
import { MenuModifierItemValidator } from "@ppvrbh/merchant-core"

interface ModifierItemFormModalProps {
    isOpen: boolean;
    form: FormInstance;
    onCloseModal: () => void;
    handleSubmit: () => void;
    isEditing?: boolean;
    isLoading?: boolean;
}

const ModifierItemFormModal = ({
    isOpen,
    form,
    onCloseModal,
    handleSubmit,
    isEditing = false,
    isLoading = false,
}: ModifierItemFormModalProps) => {
    return (
        <Modal
            wrapClassName="modifier-item-form-modal"
            style={{
                width: '400px',
                backgroundColor: Colors.white,
                borderRadius: '10px',
                border: 0,
                padding: '24px',
            }}
            visible={isOpen}
            footer={null}
            bodyStyle={{ textAlign: 'start' }}
            closable={false}
        >
            <Row align={'middle'} justify='start' style={{ marginBottom: 24 }}>
                <RBHText
                    style={{ ...textStyles.h3, color: Colors.grey800 }}
                    text={isEditing ? 'main.menu.modifieritem.edit.modal.title' : 'main.menu.modifieritem.create.modal.title'} />
            </Row>
            <Form form={form} layout='vertical' onFinish={handleSubmit}>
                <Form.Item
                    style={{ marginBottom: 16 }}
                    name='menuModifierItemName'
                    label={
                        <RBHBox style={{ ...textStyles.subtitle2 }}>
                            <FormattedMessage id='main.menu.modifieritem.form.field.name' />
                        </RBHBox>
                    }
                    rules={[
                        { required: true, message: <FormattedMessage id='main.menu.modifieritem.form.field.required' /> },
                        {   
                            pattern: MenuModifierItemValidator.menuModifierItemName.regexPattern, 
                            message: <FormattedMessage id='main.menu.modifieritem.form.field.patternInvalid' /> 
                        },
                        { 
                            max: MenuModifierItemValidator.menuModifierItemName.maxLength, 
                            message: <FormattedMessage id='main.menu.modifieritem.form.field.nameMaxLength' 
                                values={{ max: MenuModifierItemValidator.menuModifierItemName.maxLength }} /> 
                        }
                    ]}
                >
                    <RBHTextInput
                        id='create-modifierItem-name'
                        placeholder='main.menu.modifieritem.form.field.name'
                        style={{ width: '100%' }}
                    />
                </Form.Item>
                <Form.Item
                    style={{ marginBottom: 16 }}
                    name='menuModifierItemNameEN'
                    label={
                        <RBHBox style={{ ...textStyles.subtitle2 }}>
                            <FormattedMessage id='main.menu.modifieritem.form.field.nameEN' />
                        </RBHBox>
                    }
                    rules={[
                        {   
                            pattern: MenuModifierItemValidator.menuModifierItemNameEN.regexPattern, 
                            message: <FormattedMessage id='main.menu.modifieritem.form.field.nameENInvalid' /> 
                        },
                        { 
                            max: MenuModifierItemValidator.menuModifierItemNameEN.maxLength, 
                            message: <FormattedMessage id='main.menu.modifieritem.form.field.nameMaxLength' 
                                values={{ max: MenuModifierItemValidator.menuModifierItemNameEN.maxLength }} /> 
                        }
                    ]}
                >
                    <RBHTextInput
                        id='create-modifierItem-nameEN'
                        placeholder='main.menu.modifieritem.form.field.nameEN'
                        style={{ width: '100%' }}
                    />
                </Form.Item>
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            validateTrigger={['onChange', 'onBlur']}
                            style={{ marginBottom: 16 }}
                            name='menuModifierItemPrice'
                            label={
                                <RBHBox style={{ ...textStyles.subtitle2 }}>
                                    <FormattedMessage id='main.menu.modifieritem.form.field.price' />
                                </RBHBox>
                            }
                            rules={[
                                { required: true, message: <FormattedMessage id='main.menu.modifieritem.form.field.required' /> },
                                { pattern: /^(0|[1-9]\d*)$/, message: <FormattedMessage id='main.menu.modifieritem.form.field.priceInvalid' /> }
                            ]}
                        >
                            <RBHTextInput
                                id='create-modifierItem-price'
                                placeholder='main.menu.modifieritem.form.field.price'
                                style={{ width: '100%' }}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    const allowed = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
                                    if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) {
                                        e.preventDefault()
                                    }
                                    const currentValue = (e.target as HTMLInputElement).value
                                    if (/^\d$/.test(e.key) && currentValue === '0') {
                                        e.preventDefault()
                                    }
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            style={{ marginBottom: 16 }}
                            name='menuModifierItemSKUId'
                            label={
                                <RBHBox style={{ ...textStyles.subtitle2 }}>
                                    <FormattedMessage id='main.menu.modifieritem.form.field.sku' />
                                </RBHBox>
                            }
                        >
                            <RBHTextInput
                                id='create-modifierItem-sku'
                                placeholder='main.menu.modifieritem.form.field.sku'
                                style={{ width: '100%' }}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Divider style={{ margin: '24px 0' }} />
                <Row style={{ gap: 8 }} justify='end' align="middle">
                    <RBHButton
                        style={{ 
                            padding: '10px 24px', 
                            border: '1px solid #CDCDDC', 
                            borderRadius: 48,
                            color: Colors.grey800,
                            height: '100%',
                            ...textStyles.subtitle2
                        }}
                        onClick={onCloseModal}
                        text='main.button_cancel'  />
                    <Form.Item shouldUpdate noStyle>
                        {() => {
                            const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0)
                            const name = form.getFieldValue('menuModifierItemName')
                            const price = form.getFieldValue('menuModifierItemPrice')
                            const isDisabled = hasErrors || !name || price === undefined || price === ''
                            return (
                                <RBHButton
                                    htmlType='submit'
                                    style={{
                                        padding: '10px 24px',
                                        border: `1px solid ${isDisabled ? '#CDCDDC' : '#9012F5'}`,
                                        borderRadius: 48,
                                        color: Colors.white,
                                        height: '100%',
                                        backgroundColor: isDisabled ? Colors.grey400 : Colors.violet600,
                                        ...textStyles.subtitle2
                                    }}
                                    disabled={isDisabled}
                                    loading={isLoading}
                                    text='main.button_confirm'  />
                            )
                        }}
                    </Form.Item>
                </Row>
            </Form>
        </Modal>
    )
}

export default ModifierItemFormModal